"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.login = exports.parseAi = void 0;
const web_graphene_reporting_1 = require("@snapchat/web-graphene-reporting");
const web_sentry_1 = require("@snapchat/web-sentry");
const web_env_1 = require("@snapchat/web-env");
const web_grpc_1 = require("@snapchat/web-grpc");
const web_login_service_1 = require("@snapchat/ts-protos/janus/api/web_login_service");
const web_login_1 = require("@snapchat/ts-protos/janus/api/web_login");
const base64Url_1 = require("./base64Url");
const web_log_1 = require("@snapchat/web-log");
const web_i18n_1 = require("@snapchat/web-i18n");
const unauthedUnary = () => (0, web_grpc_1.unaryFactory)({
    onUnauthorizedError: () => {
        // noop - un-authed so this is only permission denied error.
        // we also have metrics on this internally.
    },
    metricsPrefix: "sidebar_login",
    hostURL: "https://accounts.snapchat.com",
});
const parseAi = (ai, isPhoneNumber) => {
    if (isPhoneNumber) {
        return { $case: "phoneNumber", phoneNumber: ai };
    }
    const isEmail = (ai) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(ai);
    if (isEmail(ai)) {
        return { $case: "email", email: ai };
    }
    else {
        return { $case: "username", username: ai };
    }
};
exports.parseAi = parseAi;
/**
 * handle Janus response
 * where we throw errors below, we show a generic error message to the user.
 * where we return a readable error string, this is shown in the UI.
 */
const checkForError = (response) => {
    const payload = response.payload;
    void (0, web_graphene_reporting_1.incrementCounter)("sidebar.janus_response", 1, {
        payload: payload?.$case ?? "none",
    });
    if (payload?.$case === "bootstrapData" ||
        response.statusCode === web_login_1.WebLoginResponse_StatusCode.SUCCESS) {
        void (0, web_graphene_reporting_1.incrementCounter)("sidebar.unexpected_success_janus_response", 1);
        throw new Error("Unexpected success response from Janus");
    }
    // these strings are translated on teh backend.
    if (payload?.$case === "errorData") {
        return payload.errorData.humanReadableErrorMessage;
    }
    if (payload?.$case === "accountDeactivationData") {
        return payload.accountDeactivationData.humanReadableMessage;
    }
    if (payload?.$case === "accountLockedData") {
        return payload.accountLockedData.humanReadableMessage;
    }
    if (payload?.$case === "bootstrapDataBrowser") {
        void (0, web_graphene_reporting_1.incrementCounter)("sidebar.unexpected_success_janus_response_web_bootstrap");
        throw new Error("Unexpected success response from Janus (Web bootstrap)");
    }
    // this should never happen, we match on all cases above.
    if (payload?.$case !== "challengeData") {
        void (0, web_graphene_reporting_1.incrementCounter)("sidebar.unexpected_janus_payload_case");
        throw new Error("Unexpected payload case");
    }
    const challengeData = payload.challengeData;
    if (challengeData.challenge?.$case !== "passwordChallenge") {
        void (0, web_graphene_reporting_1.incrementCounter)("sidebar.unsupported_challenge_type");
        throw new Error(`Unsupported challenge from Janus ${challengeData.challenge?.$case || "unknown"}`);
    }
};
/**
 * Calls Janus, validate the account identifier, and begin the sign-in process.
 * If valid, redirect to challenge page on accounts.snapchat.com
 * If there's an error, return the localised error message
 * Currently, only supported challenge is password.
 */
async function login({ ai, arkoseToken, prodEnv, isPhoneNumber, }) {
    const WEB_CHAT_URL = "https://web.snapchat.com?ref=sign_in_sidebar";
    const accountsHost = "https://accounts.snapchat.com";
    const unary = unauthedUnary();
    const webLoginServiceClient = new web_login_service_1.WebLoginServiceClientImpl({ unary });
    const parsedAi = (0, exports.parseAi)(ai, isPhoneNumber);
    const ssoClientId = "web-calling-corp--prod";
    let response;
    try {
        response = await webLoginServiceClient.WebLogin({
            webLoginHeaderBrowser: {
                arkoseToken,
                ssoClientId,
                continueParam: WEB_CHAT_URL,
            },
            loginIdentifier: parsedAi,
        }, 
        // janus returns localised error messages
        // TODO: discuss moving this to the frontend.
        (0, web_grpc_1.getMetadata)({
            "Accept-Language": (0, web_i18n_1.getLocale)(),
        }));
        const errMsg = checkForError(response);
        if (errMsg) {
            return errMsg;
        }
        // we got back password challenge, redirect to the accounts password page.
        const authSession = response.authenticationSessionPayload;
        const authSessionBase64Url = (0, base64Url_1.base64EncodeURL)(authSession);
        const base64Ai = btoa(ai);
        void (0, web_graphene_reporting_1.incrementCounter)("sidebar.sign_in_redirect", 1);
        await (0, web_graphene_reporting_1.flushMetrics)();
        const encodedReferrer = encodeURIComponent(WEB_CHAT_URL);
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        (0, web_env_1.getWindow)().location.href = `${accountsHost}/accounts/v2/password?ai=${base64Ai}&as=${authSessionBase64Url}&continue=${encodeURIComponent(`/accounts/sso?client_id=${ssoClientId}&referrer=${encodedReferrer}`)}&referrer=${encodedReferrer}`;
    }
    catch (e) {
        if (!prodEnv) {
            web_log_1.log.error("[Login]", e);
        }
        (0, web_sentry_1.logSentryError)("Sidebar login failed", e);
        void (0, web_graphene_reporting_1.incrementCounter)("sidebar.sign_in_failure", 1);
        throw e;
    }
}
exports.login = login;
//# sourceMappingURL=login.js.map